import * as React from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { Loading } from "@/components/ui/loading";

export interface EmptyStateProps {
  icon?: React.ReactNode;
  /** Wrap icon in a styled container (gradient background) */
  iconWrapper?: "none" | "gradient-emerald" | "gradient-amber" | "gradient-blue" | "muted";
  title?: string;
  description: string;
  action?: {
    label: string;
    href?: string;
    onClick?: () => void;
    icon?: React.ReactNode;
    variant?: "default" | "outline" | "secondary";
  };
  /** Secondary action button */
  secondaryAction?: {
    label: string;
    href?: string;
    onClick?: () => void;
  };
  /** Additional content below the action buttons */
  children?: React.ReactNode;
  className?: string;
  variant?: "default" | "card" | "inline" | "fullPage";
  size?: "sm" | "default" | "lg";
  /** Show loading state instead of content */
  loading?: boolean;
  /** Loading text */
  loadingText?: string;
}

const iconWrapperClasses = {
  none: "",
  muted: "bg-muted rounded-full p-4",
  "gradient-emerald": "bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full p-4 shadow-lg text-white",
  "gradient-amber": "bg-gradient-to-br from-amber-500 to-orange-600 rounded-full p-4 shadow-lg text-white",
  "gradient-blue": "bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full p-4 shadow-lg text-white",
};

export function EmptyState({
  icon,
  iconWrapper = "none",
  title,
  description,
  action,
  secondaryAction,
  children,
  className,
  variant = "default",
  size = "default",
  loading = false,
  loadingText = "Loading...",
}: EmptyStateProps) {
  const sizeClasses = {
    sm: "py-6",
    default: "py-10",
    lg: "py-16",
  };

  const iconSizeClasses = {
    sm: "h-8 w-8",
    default: "h-12 w-12",
    lg: "h-16 w-16",
  };

  const textSizeClasses = {
    sm: {
      title: "text-base",
      description: "text-sm",
    },
    default: {
      title: "text-lg",
      description: "text-base",
    },
    lg: {
      title: "text-xl md:text-2xl",
      description: "text-lg",
    },
  };

  // Loading state
  if (loading) {
    return (
      <div className={cn(
        "flex flex-col items-center justify-center text-center",
        variant === "fullPage" ? "min-h-screen" : sizeClasses[size],
        className
      )}>
        <Loading size="lg" className="mb-4" />
        <p className="text-muted-foreground">{loadingText}</p>
      </div>
    );
  }

  const renderIcon = () => {
    if (!icon) return null;
    
    const iconElement = (
      <div className={cn(
        iconWrapper === "none" ? "text-muted-foreground" : "",
        iconWrapper === "none" ? iconSizeClasses[size] : ""
      )}>
        {icon}
      </div>
    );

    if (iconWrapper === "none") {
      return (
        <div className="flex justify-center mb-4">
          {iconElement}
        </div>
      );
    }

    return (
      <div className="flex justify-center mb-6">
        <div className={cn(iconWrapperClasses[iconWrapper])}>
          {icon}
        </div>
      </div>
    );
  };

  const renderActions = () => {
    if (!action && !secondaryAction) return null;

    const renderButton = (
      btnAction: typeof action,
      isPrimary: boolean
    ) => {
      if (!btnAction) return null;
      
      const buttonVariant = isPrimary 
        ? (btnAction.variant || "default")
        : "outline";

      const buttonContent = (
        <>
          {btnAction.icon && <span className="mr-2">{btnAction.icon}</span>}
          {btnAction.label}
        </>
      );

      if (btnAction.href) {
        return (
          <Button asChild variant={buttonVariant} size={size === "lg" ? "lg" : "default"}>
            <Link href={btnAction.href}>{buttonContent}</Link>
          </Button>
        );
      }

      return (
        <Button
          variant={buttonVariant}
          size={size === "lg" ? "lg" : "default"}
          onClick={btnAction.onClick}
        >
          {buttonContent}
        </Button>
      );
    };

    return (
      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-4">
        {renderButton(action, true)}
        {renderButton(secondaryAction, false)}
      </div>
    );
  };

  const content = (
    <div className={cn(
      "text-center",
      variant === "fullPage" ? "max-w-md mx-auto" : "",
      variant !== "fullPage" && sizeClasses[size],
      className
    )}>
      {renderIcon()}
      
      {title && (
        <h3 className={cn(
          "font-bold text-foreground mb-2",
          textSizeClasses[size].title
        )}>
          {title}
        </h3>
      )}
      
      <p className={cn(
        "text-muted-foreground",
        action || secondaryAction || children ? "mb-4" : "",
        textSizeClasses[size].description
      )}>
        {description}
      </p>
      
      {renderActions()}
      
      {children && (
        <div className="mt-8">
          {children}
        </div>
      )}
    </div>
  );

  if (variant === "fullPage") {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        {content}
      </div>
    );
  }

  if (variant === "card") {
    return (
      <Card>
        <CardContent className="pt-6">
          {content}
        </CardContent>
      </Card>
    );
  }

  if (variant === "inline") {
    return (
      <div className={cn("text-center py-4", className)}>
        <p className="text-sm text-muted-foreground">
          {description}
        </p>
        {action && (
          <div className="mt-2">
            {action.href ? (
              <Button asChild variant="link" size="sm">
                <Link href={action.href}>
                  {action.label}
                </Link>
              </Button>
            ) : (
              <Button
                variant="link"
                size="sm"
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  return content;
}
